import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import { styles, colors, radius } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import useUrlState from '../../hooks/useUrlState';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import FiyatBandKontrolu from './FiyatBandKontrolu';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// EBITDA / P&L raporu bucket'ları — gider kategorileri buralardan birine atanır
const PNL_BUCKETS = [
  { value: '', label: '— (atanmamış)' },
  { value: 'PERSONNEL', label: 'Personel (OPEX)' },
  { value: 'RENT', label: 'Kira (OPEX)' },
  { value: 'UTILITIES', label: 'Enerji & Telekom (OPEX)' },
  { value: 'MARKETING', label: 'Pazarlama & Dijital (OPEX)' },
  { value: 'OPERATIONS', label: 'Operasyon — Bakım/Sarf/Lojistik (OPEX)' },
  { value: 'G_AND_A', label: 'Genel Yönetim (OPEX)' },
  { value: 'OTHER_OPEX', label: 'Diğer Faaliyet Gideri (OPEX)' },
  { value: 'D_AND_A', label: 'Amortisman — D&A (EBITDA altı)' },
  { value: 'FINANCE', label: 'Finansman — Faiz/Komisyon (EBIT altı)' },
];

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const MasterVerilerPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const canManagePriceBand = hasPermission(user?.role, PERMISSIONS.MANAGE_PRICE_BAND, user?.effectivePermissions);
  // Aktif sekme (hangi master tablo) URL'e bağlı: başka sayfaya gidip geri dönünce
  // / yenileyince aynı master tablo açık kalır (menüden açınca varsayılan: malzemeler).
  const [activeTab, setActiveTab] = useUrlState('tab', 'malzemeler');
  const [malzemeler, setMalzemeler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [giderKategorileri, setGiderKategorileri] = useState([]);
  const [zayiNedenleri, setZayiNedenleri] = useState([]);
  const [urunTurleri, setUrunTurleri] = useState([]);
  const [stopajTurleri, setStopajTurleri] = useState([]);
  const [departmanlar, setDepartmanlar] = useState([]);
  const [markalar, setMarkalar] = useState([]);
  const [kartlar, setKartlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [malzemeForm, setMalzemeForm] = useState({ kod: '', ad: '', birim: 'KG', kategori: 'Et', min_stok: '', marka_id: '', urun_turu_id: '' });
  const [lokasyonForm, setLokasyonForm] = useState({ ad: '', tip: 'DEPO', marka_id: '' });
  const [firmaForm, setFirmaForm] = useState({ kod: '', ad: '', tip: 'SIRKET', vergi_no: '', vergi_dairesi: '', adres: '', kdv_orani: '' });
  const [giderKatForm, setGiderKatForm] = useState({ kod: '', ad: '', renk: '#6c757d', tdhp_borc: '770', tdhp_alacak: '102', keywords: '', pnl_bucket: '' });
  const [zayiNedenForm, setZayiNedenForm] = useState({ kod: '', ad: '', aciklama: '', renk: '#6c757d', sira: '' });
  const [urunTuruForm, setUrunTuruForm] = useState({ kod: '', ad: '', skt_sari_gun: '', skt_kirmizi_gun: '', devir_gun_esik: '', olu_stok_gun: '', sira: '' });
  const [stopajForm, setStopajForm] = useState({ kod: '', ad: '', oran: '', sira: '' });
  const [departmanForm, setDepartmanForm] = useState({ kod: '', ad: '' });
  const [markaForm, setMarkaForm] = useState({ kod: '', ad: '', gunluk_calisma_saati: 8 });
  const [kartForm, setKartForm] = useState({ kod: '', son_dort_hane: '', banka: '', kesim_gunu: '', son_odeme_gunu: '', takip_baslangic_tarihi: '' });
  const [odemeYontemleri, setOdemeYontemleri] = useState([]);
  const [odemeYontemiForm, setOdemeYontemiForm] = useState({
    kod: '', ad: '', sira: 0, nakit_mi: false, cek_ister: false,
    kart_ister: false, bakiye_azaltir: true, stok_etkisi: 'NONE',
    tdhp_borc: '320', tdhp_alacak: '',
  });

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  // Düzenleme panelinde marka-bazlı güncel SAS (iç satış) fiyatları — malzemeId → liste (lazy fetch)
  const [sasFiyatlar, setSasFiyatlar] = useState({});
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Master veri düzenleme modal state (firma/kategori/departman ortak)
  const [editModal, setEditModal] = useState(null); // { type: 'firma'|'kategori'|'departman', data: {...} }

  useEffect(() => {
    fetchData();
  }, []);

  // setLoading(true) YOK: loading yalnız ilk mount'ta true (useState(true)).
  // Kayıt/silme sonrası refetch'ler sessiz arka plan yenilemesidir — full-page
  // "Yükleniyor..." dönüşü tabloyu unmount edip scroll/bağlamı sıfırlıyordu.
  const fetchData = async () => {
    try {
      const [malzRes, lokRes, firmaRes, katRes, zayiRes, urunTuruRes, stopajRes, depRes, markaRes, kartRes, odemeRes] = await Promise.all([
        fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() }),
        // include_inactive=true: pasifler de gözüksün; with_usage=true: kullanım sayısı
        fetch(`${API_URL}/api/firmalar?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/gider-kategorileri?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/zayi-nedenleri?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/urun-turleri?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/stopaj-turleri?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/departmanlar?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/markalar?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/kredi-kartlari?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/odeme-yontemleri?include_inactive=true&with_usage=true`, { headers: getAuthHeaders() }),
      ]);
      const malzData = await malzRes.json();
      const lokData = await lokRes.json();
      const firmaData = await firmaRes.json().catch(() => []);
      const katData = await katRes.json().catch(() => []);
      const zayiData = await zayiRes.json().catch(() => []);
      const urunTuruData = await urunTuruRes.json().catch(() => []);
      const stopajData = await stopajRes.json().catch(() => []);
      const depData = await depRes.json().catch(() => []);
      const markaData = await markaRes.json().catch(() => []);
      const kartData = await kartRes.json().catch(() => []);
      const odemeData = await odemeRes.json().catch(() => []);
      setMalzemeler(Array.isArray(malzData) ? malzData : []);
      setLokasyonlar(Array.isArray(lokData) ? lokData : []);
      setFirmalar(Array.isArray(firmaData) ? firmaData : []);
      setGiderKategorileri(Array.isArray(katData) ? katData : []);
      setZayiNedenleri(Array.isArray(zayiData) ? zayiData : []);
      setUrunTurleri(Array.isArray(urunTuruData) ? urunTuruData : []);
      setStopajTurleri(Array.isArray(stopajData) ? stopajData : []);
      setDepartmanlar(Array.isArray(depData) ? depData : []);
      setMarkalar(Array.isArray(markaData) ? markaData : []);
      setKartlar(Array.isArray(kartData) ? kartData : []);
      setOdemeYontemleri(Array.isArray(odemeData) ? odemeData : []);
    } catch (err) {
      logError('Veri yüklenemedi:', err);
    }
    setLoading(false);
  };

  // ─── Firma CRUD ────────────────────────────────────────────────
  const handleSaveFirma = async () => {
    if (!firmaForm.kod || !firmaForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const payload = {
        ...firmaForm,
        // Boş string → null (genel KDV oranı kullanılır); 0 geçerli %0 oran, null'a çevirme.
        kdv_orani: firmaForm.kdv_orani === '' ? null : Number(firmaForm.kdv_orani),
      };
      const res = await fetch(`${API_URL}/api/firmalar`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Firma eklendi');
      setFirmaForm({ kod: '', ad: '', tip: 'SIRKET', vergi_no: '', vergi_dairesi: '', adres: '', kdv_orani: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteFirma = async (id) => {
    if (!window.confirm('Firma pasifleştirilsin mi? (silinmez)')) return;
    try {
      const res = await fetch(`${API_URL}/api/firmalar/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Hata');
      toast.success('Firma pasifleştirildi');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Gider Kategorisi CRUD ─────────────────────────────────────
  const handleSaveGiderKat = async () => {
    if (!giderKatForm.kod || !giderKatForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const res = await fetch(`${API_URL}/api/gider-kategorileri`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(giderKatForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Kategori eklendi');
      setGiderKatForm({ kod: '', ad: '', renk: '#6c757d', tdhp_borc: '770', tdhp_alacak: '102', keywords: '', pnl_bucket: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleGiderKat = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/gider-kategorileri/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Zayi Nedeni CRUD ──────────────────────────────────────────
  const handleSaveZayiNeden = async () => {
    if (!zayiNedenForm.kod || !zayiNedenForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const payload = {
        kod: zayiNedenForm.kod,
        ad: zayiNedenForm.ad,
        aciklama: zayiNedenForm.aciklama,
        renk: zayiNedenForm.renk,
        sira: zayiNedenForm.sira !== '' ? parseInt(zayiNedenForm.sira, 10) || 0 : 0,
      };
      const res = await fetch(`${API_URL}/api/zayi-nedenleri`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Zayi nedeni eklendi');
      setZayiNedenForm({ kod: '', ad: '', aciklama: '', renk: '#6c757d', sira: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleZayiNeden = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/zayi-nedenleri/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Ürün Türü CRUD ────────────────────────────────────────────
  const handleSaveUrunTuru = async () => {
    if (!urunTuruForm.kod || !urunTuruForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const intOrUndef = (v) => (v !== '' && v != null ? parseInt(v, 10) : undefined);
      const payload = {
        kod: urunTuruForm.kod,
        ad: urunTuruForm.ad,
        skt_sari_gun: intOrUndef(urunTuruForm.skt_sari_gun),
        skt_kirmizi_gun: intOrUndef(urunTuruForm.skt_kirmizi_gun),
        devir_gun_esik: intOrUndef(urunTuruForm.devir_gun_esik),
        olu_stok_gun: intOrUndef(urunTuruForm.olu_stok_gun),
        sira: urunTuruForm.sira !== '' ? parseInt(urunTuruForm.sira, 10) || 0 : 0,
      };
      // Boş bırakılan eşikler backend varsayılanlarına düşsün (undefined gönderme)
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
      const res = await fetch(`${API_URL}/api/urun-turleri`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Ürün türü eklendi');
      setUrunTuruForm({ kod: '', ad: '', skt_sari_gun: '', skt_kirmizi_gun: '', devir_gun_esik: '', olu_stok_gun: '', sira: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleUrunTuru = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/urun-turleri/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Stopaj Türü CRUD ──────────────────────────────────────────
  const handleSaveStopaj = async () => {
    if (!stopajForm.kod || !stopajForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    const oran = parseFloat(stopajForm.oran);
    if (!(oran >= 0)) { toast.warning('Oran 0 veya daha büyük olmalı'); return; }
    try {
      const payload = {
        kod: stopajForm.kod,
        ad: stopajForm.ad,
        oran,
        sira: stopajForm.sira !== '' ? parseInt(stopajForm.sira, 10) || 0 : 0,
      };
      const res = await fetch(`${API_URL}/api/stopaj-turleri`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Stopaj türü eklendi');
      setStopajForm({ kod: '', ad: '', oran: '', sira: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleStopaj = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/stopaj-turleri/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Departman CRUD ────────────────────────────────────────────
  const handleSaveDepartman = async () => {
    if (!departmanForm.kod || !departmanForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const res = await fetch(`${API_URL}/api/departmanlar`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(departmanForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Departman eklendi');
      setDepartmanForm({ kod: '', ad: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleDepartman = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/departmanlar/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Marka CRUD ────────────────────────────────────────────────
  const handleSaveMarka = async () => {
    if (!markaForm.kod || !markaForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    const gcs = parseFloat(markaForm.gunluk_calisma_saati);
    if (!(gcs > 0 && gcs <= 24)) { toast.warning('Günlük çalışma saati 0-24 arası olmalı'); return; }
    try {
      const res = await fetch(`${API_URL}/api/markalar`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...markaForm, gunluk_calisma_saati: gcs }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Marka eklendi');
      setMarkaForm({ kod: '', ad: '', gunluk_calisma_saati: 8 });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleMarka = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/markalar/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // "" → null, sayı → 1-31 kontrolü. Geçersizse hata mesajı döner (null=valid).
  const gunVeyaNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    return Number.isInteger(n) && n >= 1 && n <= 31 ? n : 'INVALID';
  };

  const handleSaveKart = async () => {
    if (!kartForm.kod || !kartForm.son_dort_hane) { toast.warning('Kod ve son 4 hane zorunludur'); return; }
    if (!/^\d{4}$/.test(kartForm.son_dort_hane)) { toast.warning('Son 4 hane 4 rakam olmalı'); return; }
    const kesim = gunVeyaNull(kartForm.kesim_gunu);
    const sonOdeme = gunVeyaNull(kartForm.son_odeme_gunu);
    if (kesim === 'INVALID' || sonOdeme === 'INVALID') { toast.warning('Kesim/son ödeme günü 1-31 arası olmalı'); return; }
    try {
      const res = await fetch(`${API_URL}/api/kredi-kartlari`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...kartForm, kesim_gunu: kesim, son_odeme_gunu: sonOdeme,
          // Boş string → null (TARİH, gün değil; gunVeyaNull kullanma).
          takip_baslangic_tarihi: kartForm.takip_baslangic_tarihi || null,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Kart eklendi');
      setKartForm({ kod: '', son_dort_hane: '', banka: '', kesim_gunu: '', son_odeme_gunu: '', takip_baslangic_tarihi: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleKart = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/kredi-kartlari/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Ödeme Yöntemi CRUD ────────────────────────────────────────
  const handleSaveOdemeYontemi = async () => {
    if (!odemeYontemiForm.kod || !odemeYontemiForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const res = await fetch(`${API_URL}/api/odeme-yontemleri`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(odemeYontemiForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Ödeme yöntemi eklendi');
      setOdemeYontemiForm({ kod: '', ad: '', sira: 0, nakit_mi: false, cek_ister: false, kart_ister: false, bakiye_azaltir: true, stok_etkisi: 'NONE', tdhp_borc: '320', tdhp_alacak: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggleOdemeYontemi = async (id, aktif) => {
    try {
      const res = await fetch(`${API_URL}/api/odeme-yontemleri/${id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ aktif: !aktif }),
      });
      if (!res.ok) throw new Error('Hata');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Düzenle Modal ─────────────────────────────────────────────
  const openEditModal = (type, item) => {
    setEditModal({ type, data: { ...item } });
  };

  const saveEditModal = async () => {
    if (!editModal) return;
    const { type, data } = editModal;
    const endpoints = { firma: 'firmalar', kategori: 'gider-kategorileri', 'zayi-neden': 'zayi-nedenleri', 'urun-turu': 'urun-turleri', 'stopaj-turu': 'stopaj-turleri', departman: 'departmanlar', marka: 'markalar', kart: 'kredi-kartlari', 'odeme-yontemi': 'odeme-yontemleri' };
    let body = data;
    if (type === 'kart') {
      const kesim = gunVeyaNull(data.kesim_gunu);
      const sonOdeme = gunVeyaNull(data.son_odeme_gunu);
      if (kesim === 'INVALID' || sonOdeme === 'INVALID') { toast.warning('Kesim/son ödeme günü 1-31 arası olmalı'); return; }
      body = {
        ...data, kesim_gunu: kesim, son_odeme_gunu: sonOdeme,
        // Boş string → null (TARİH alanı; temizleme desteklenir).
        takip_baslangic_tarihi: data.takip_baslangic_tarihi || null,
      };
    }
    try {
      const res = await fetch(`${API_URL}/api/${endpoints[type]}/${data.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Hata'); }
      toast.success('Güncellendi');
      setEditModal(null);
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Kalıcı Sil (hard delete) ──────────────────────────────────
  const hardDelete = async (type, item) => {
    const endpoints = { firma: 'firmalar', kategori: 'gider-kategorileri', 'zayi-neden': 'zayi-nedenleri', 'urun-turu': 'urun-turleri', 'stopaj-turu': 'stopaj-turleri', departman: 'departmanlar', marka: 'markalar', kart: 'kredi-kartlari', 'odeme-yontemi': 'odeme-yontemleri' };
    const labels = { firma: 'firma', kategori: 'kategori', 'zayi-neden': 'zayi nedeni', 'urun-turu': 'ürün türü', 'stopaj-turu': 'stopaj türü', departman: 'departman', marka: 'marka', kart: 'kart', 'odeme-yontemi': 'ödeme yöntemi' };
    if (!window.confirm(`"${item.ad}" ${labels[type]}'ı KALICI olarak silinecek. Geri alınamaz. Emin misin?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/${endpoints[type]}/${item.id}?hard=true`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Silinemedi');
      }
      toast.success('Kalıcı olarak silindi');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleSaveMalzeme = async () => {
    if (!malzemeForm.kod || !malzemeForm.ad) { toast.warning('Kod ve ad zorunludur'); return; }
    try {
      const payload = {
        ...malzemeForm,
        min_stok: parseFloat(malzemeForm.min_stok) || 0,
        marka_id: malzemeForm.marka_id ? parseInt(malzemeForm.marka_id, 10) : null,
        urun_turu_id: malzemeForm.urun_turu_id ? parseInt(malzemeForm.urun_turu_id, 10) : null,
      };
      const res = await fetch(`${API_URL}/api/malzemeler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        invalidateQueries(['stok', 'dashboard']);
        toast.success('Malzeme eklendi');
        setMalzemeForm({ kod: '', ad: '', birim: 'KG', kategori: 'Et', min_stok: '', marka_id: '', urun_turu_id: '' });
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Malzeme eklenemedi');
      }
    } catch (err) { logError(err); }
  };

  const handleSaveLokasyon = async () => {
    if (!lokasyonForm.ad) { toast.warning('Ad zorunludur'); return; }
    try {
      const payload = {
        ...lokasyonForm,
        marka_id: lokasyonForm.marka_id ? parseInt(lokasyonForm.marka_id, 10) : null,
      };
      const res = await fetch(`${API_URL}/api/lokasyonlar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        invalidateQueries(['stok', 'dashboard']);
        toast.success('Lokasyon eklendi');
        setLokasyonForm({ ad: '', tip: 'DEPO', marka_id: '' });
        fetchData();
      }
    } catch (err) { logError(err); }
  };

  const handleUpdateLokasyonMarka = async (lokasyonId, markaId) => {
    try {
      const res = await fetch(`${API_URL}/api/lokasyonlar/${lokasyonId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ marka_id: markaId ? parseInt(markaId, 10) : null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Güncelleme başarısız');
      }
      toast.success('Lokasyon markası güncellendi');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // Üretim merkezi (commissary) bayrağı — üretici firma bundan türetilir (Anayasa M.4)
  const handleUpdateLokasyonUretim = async (lokasyonId, uretim) => {
    try {
      const res = await fetch(`${API_URL}/api/lokasyonlar/${lokasyonId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ uretim }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Güncelleme başarısız');
      }
      toast.success(uretim ? 'Üretim merkezi olarak işaretlendi' : 'Üretim merkezi kaldırıldı');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  // --- Inline Edit ---
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({
      kod: m.kod || '',
      ad: m.ad || '',
      birim: m.birim || 'KG',
      kategori: m.kategori || '',
      fiyat: m.fiyat ?? 0,
      min_stok: m.min_stok ?? 0,
      min_stok_kilit: m.min_stok_kilit ?? false,
      raf_omru_gun: m.raf_omru_gun ?? '',
      tedarik_suresi_gun: m.tedarik_suresi_gun ?? '',
      urun_turu_id: m.urun_turu_id ?? '',
      marka_ids: m.marka_ids || (m.marka_id ? [m.marka_id] : [])
    });
    // SAS (iç satış) fiyatlarını lazy çek — yalnız düzenlemeye girince (452 malzeme önden yüklenmez)
    fetch(`${API_URL}/api/malzemeler/${m.id}/ic-satis-fiyatlari`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setSasFiyatlar(prev => ({ ...prev, [m.id]: Array.isArray(d) ? d : [] })))
      .catch(() => setSasFiyatlar(prev => ({ ...prev, [m.id]: [] })));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Düzenlenen malzeme satırının altındaki SAS (iç satış) fiyat paneli — salt-okunur.
  // Fiyat satış işlemlerinden türetilir (backend); 'kayıt yok' = SAS~ tahmin durumu.
  const renderSasPaneli = (malzemeId) => {
    const rows = sasFiyatlar[malzemeId];
    const kaynakLabel = { FATURA: 'Fatura', TRANSFER: 'Transfer', DOGRUDAN: 'Doğrudan alım' };
    return (
      <div>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#006064', marginBottom: '6px' }}>
          İç Satış (SAS) Fiyatları{' '}
          <span style={{ fontWeight: 'normal', color: '#888' }}>— marka bazında güncel, salt okunur (şirketler arası satış)</span>
        </div>
        {rows == null ? (
          <div style={{ fontSize: '12px', color: '#888' }}>Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: '12px', color: '#888' }}>Aktif marka yok.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ color: '#555' }}>
                <th style={{ textAlign: 'left', padding: '3px 16px 3px 0' }}>Marka</th>
                <th style={{ textAlign: 'right', padding: '3px 16px' }}>Güncel Fiyat</th>
                <th style={{ textAlign: 'left', padding: '3px 16px' }}>Tarih</th>
                <th style={{ textAlign: 'left', padding: '3px 0' }}>Kaynak</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const yok = r.fiyat == null;
                return (
                  <tr key={r.marka_kod}>
                    <td style={{ padding: '3px 16px 3px 0', color: yok ? '#aaa' : '#222' }}>{r.marka_ad}</td>
                    <td style={{ textAlign: 'right', padding: '3px 16px', fontFamily: 'monospace', color: yok ? '#aaa' : (r.kaynak_tip === 'DOGRUDAN' ? '#2e7d32' : '#006064'), fontWeight: yok ? 'normal' : 'bold' }}>
                      {yok ? 'kayıt yok' : `${Number(r.fiyat).toFixed(2)} TL`}
                    </td>
                    <td style={{ padding: '3px 16px', color: yok ? '#aaa' : '#555' }}>{r.tarih ? String(r.tarih).slice(0, 10) : '—'}</td>
                    <td style={{ padding: '3px 0' }}>
                      {r.kaynak_tip === 'DOGRUDAN'
                        ? <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: '#e8f5e9', color: '#2e7d32' }}>Doğrudan alım</span>
                        : <span style={{ color: yok ? '#aaa' : '#555' }}>{r.kaynak_tip ? (kaynakLabel[r.kaynak_tip] || r.kaynak_tip) : '—'}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const handleEditSave = async () => {
    if (!editForm.ad) { toast.warning('Malzeme adi zorunludur'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/malzemeler/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          ...editForm,
          fiyat: parseFloat(editForm.fiyat) || 0,
          min_stok: parseFloat(editForm.min_stok) || 0,
          min_stok_kilit: !!editForm.min_stok_kilit,
          raf_omru_gun: editForm.raf_omru_gun !== '' ? parseInt(editForm.raf_omru_gun) : null,
          tedarik_suresi_gun: editForm.tedarik_suresi_gun !== '' ? parseInt(editForm.tedarik_suresi_gun) : null,
          urun_turu_id: editForm.urun_turu_id !== '' && editForm.urun_turu_id != null ? parseInt(editForm.urun_turu_id, 10) : null,
          markalar: (editForm.marka_ids || []).map(x => parseInt(x, 10))
        })
      });
      if (res.ok) {
        invalidateQueries(['stok', 'dashboard']);
        setEditingId(null);
        setEditForm({});
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Guncelleme başarısız');
      }
    } catch (err) {
      logError(err);
      toast.error('Guncelleme hatası');
    }
    setSaving(false);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`"${m.ad}" malzemesini silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/malzemeler/${m.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) {
        invalidateQueries(['stok', 'dashboard']);
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || '';
        if (detail.includes('kullaniliyor')) {
          const forceConfirm = window.confirm(`${detail}\n\nBagli kayitlarla birlikte silmek istiyor musunuz?`);
          if (forceConfirm) {
            const res2 = await fetch(`${API_URL}/api/malzemeler/${m.id}?force=true`, { method: 'DELETE', headers: getAuthHeaders() });
            if (res2.ok) {
              invalidateQueries(['stok', 'dashboard']);
              fetchData();
            } else {
              const err2 = await res2.json().catch(() => ({}));
              toast.error(err2.detail || 'Silme başarısız');
            }
          }
        } else {
          toast.error(detail || 'Silme başarısız');
        }
      }
    } catch (err) {
      logError(err);
      toast.error('Silme hatası');
    }
  };

  if (loading) {
    return (
      <div>
        <Header title="Master Veriler" />
        <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  const inputStyle = { width: '100%', padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' };

  return (
    <div>
      <Header title="Master Veriler" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { key: 'malzemeler', label: `Malzemeler (${malzemeler.length})` },
            { key: 'lokasyonlar', label: `Lokasyonlar (${lokasyonlar.length})` },
            { key: 'firmalar', label: `Firmalar (${firmalar.length})` },
            { key: 'gider-kategorileri', label: `Gider Kategorileri (${giderKategorileri.length})` },
            { key: 'zayi-nedenleri', label: `Zayi Nedenleri (${zayiNedenleri.length})` },
            { key: 'urun-turleri', label: `Ürün Türleri (${urunTurleri.length})` },
            { key: 'stopaj-turleri', label: `Stopaj Türleri (${stopajTurleri.length})` },
            { key: 'departmanlar', label: `Departmanlar (${departmanlar.length})` },
            { key: 'markalar', label: `Markalar (${markalar.length})` },
            { key: 'kredi-kartlari', label: `Kredi Kartları (${kartlar.length})` },
            { key: 'odeme-yontemleri', label: `Ödeme Yöntemleri (${odemeYontemleri.length})` },
            ...(canManagePriceBand ? [{ key: 'fiyat-band', label: 'Fiyat Band Kontrolü' }] : []),
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: '10px 20px', background: activeTab === t.key ? styles.primary : 'white', color: activeTab === t.key ? 'white' : '#333', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md }}>

          {activeTab === 'malzemeler' && (
            <div>
              <div style={{ marginBottom: '30px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Malzeme Ekle</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '10px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: '12px' }}>Kod</label>
                    <input style={{ width: '100%', padding: '8px' }} value={malzemeForm.kod} onChange={e => setMalzemeForm({ ...malzemeForm, kod: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }}>Malzeme Adi</label>
                    <input style={{ width: '100%', padding: '8px' }} value={malzemeForm.ad} onChange={e => setMalzemeForm({ ...malzemeForm, ad: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }}>Birim</label>
                    <select style={{ width: '100%', padding: '8px' }} value={malzemeForm.birim} onChange={e => setMalzemeForm({ ...malzemeForm, birim: e.target.value })}>
                      <option value="KG">KG</option>
                      <option value="AD">AD</option>
                      <option value="LT">LT</option>
                      <option value="GR">GR</option>
                      <option value="ML">ML</option>
                      <option value="PAKET">PAKET</option>
                      <option value="KUTU">KUTU</option>
                      <option value="PORSIYON">PORSIYON</option>
                      <option value="METRE">METRE</option>
                      <option value="RULO">RULO</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }}>Kategori</label>
                    <select style={{ width: '100%', padding: '8px' }} value={malzemeForm.kategori} onChange={e => setMalzemeForm({ ...malzemeForm, kategori: e.target.value })}>
                      <option value="Et">Et</option>
                      <option value="Sebze">Sebze</option>
                      <option value="Ambalaj">Ambalaj</option>
                      <option value="Sarf">Sarf</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }} title="Boş bırakırsanız tüm şubelerde görünür (paylaşılan / hammadde gibi). Bir marka seçerseniz sadece o markanın şubelerinde görünür.">
                      Marka <span style={{ color: '#888' }}>(opsiyonel)</span>
                    </label>
                    <select style={{ width: '100%', padding: '8px' }} value={malzemeForm.marka_id} onChange={e => setMalzemeForm({ ...malzemeForm, marka_id: e.target.value })}>
                      <option value="">— Paylaşılan (hepsi)</option>
                      {markalar.filter(m => m.aktif).map(m => (
                        <option key={m.id} value={m.id}>{m.ad}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }} title="SKT/devir eşiklerini belirleyen ürün türü. Boş = atanmamış.">
                      Ürün Türü <span style={{ color: '#888' }}>(opsiyonel)</span>
                    </label>
                    <select style={{ width: '100%', padding: '8px' }} value={malzemeForm.urun_turu_id} onChange={e => setMalzemeForm({ ...malzemeForm, urun_turu_id: e.target.value })}>
                      <option value="">— Atanmamış</option>
                      {urunTurleri.filter(t => t.aktif).map(t => (
                        <option key={t.id} value={t.id}>{t.ad}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }}>Min Stok</label>
                    <input type="number" style={{ width: '100%', padding: '8px' }} value={malzemeForm.min_stok} onChange={e => setMalzemeForm({ ...malzemeForm, min_stok: e.target.value })} />
                  </div>
                  <button onClick={handleSaveMalzeme} style={{ padding: '10px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
                  💡 <strong>Marka boş</strong> → tüm şubelerde görünür (hammadde, ambalaj gibi paylaşılan ürünler için).
                  Marka seçerseniz <strong>sadece o markanın</strong> şubelerinde görünür.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0 }}>Mevcut Malzemeler ({malzemeler.length})</h4>
                <span style={{ fontSize: '12px', color: '#f57f17', background: '#fff8e1', padding: '4px 12px', borderRadius: radius.sm }}>
                  &#9998; Satıra tıklayarak düzenleyebilirsiniz
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${colors.border}`, borderRadius: radius.md, fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left', background: colors.backgroundAlt }}>
                    <th style={{ padding: '10px' }}>Kod</th>
                    <th style={{ padding: '10px' }}>Ad</th>
                    <th style={{ padding: '10px' }}>Birim</th>
                    <th style={{ padding: '10px' }}>Kategori</th>
                    <th style={{ padding: '10px' }}>Ürün Türü</th>
                    <th style={{ padding: '10px' }}>Marka</th>
                    <th style={{ padding: '7px', textAlign: 'right' }}>Fiyat (TL)</th>
                    <th style={{ padding: '7px', textAlign: 'right' }}>Min Stok</th>
                    <th style={{ padding: '7px', textAlign: 'right' }}>Raf Ömrü (gün)</th>
                    <th style={{ padding: '7px', textAlign: 'right' }}>Tedarik (gün)</th>
                    <th style={{ padding: '7px', textAlign: 'center', width: '160px' }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {malzemeler.map(m => {
                    const isEditing = editingId === m.id;
                    return (
                      <React.Fragment key={m.id}>
                      <tr
                          onClick={() => !isEditing && startEdit(m)}
                          style={{
                            borderBottom: `1px solid ${colors.border}`,
                            background: isEditing ? '#fff9c4' : 'transparent',
                            cursor: !isEditing ? 'pointer' : 'default',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background = '#e3f2fd'; }}
                          onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = 'transparent'; }}>
                        {isEditing ? (
                          <>
                            <td style={{ padding: '6px' }}>
                              <input style={{ ...inputStyle, border: '2px solid #1565c0' }} value={editForm.kod} onChange={e => setEditForm({ ...editForm, kod: e.target.value })} />
                            </td>
                            <td style={{ padding: '6px' }}>
                              <input style={{ ...inputStyle, border: '2px solid #1565c0' }} autoFocus value={editForm.ad} onChange={e => setEditForm({ ...editForm, ad: e.target.value })} />
                            </td>
                            <td style={{ padding: '6px' }}>
                              <select style={{ ...inputStyle, border: '2px solid #1565c0' }} value={editForm.birim} onChange={e => setEditForm({ ...editForm, birim: e.target.value })}>
                                <option value="KG">KG</option>
                                <option value="AD">AD</option>
                                <option value="LT">LT</option>
                                <option value="GR">GR</option>
                                <option value="ML">ML</option>
                                <option value="PAKET">PAKET</option>
                                <option value="KUTU">KUTU</option>
                                <option value="PORSIYON">PORSIYON</option>
                                <option value="METRE">METRE</option>
                                <option value="RULO">RULO</option>
                              </select>
                            </td>
                            <td style={{ padding: '6px' }}>
                              <input style={{ ...inputStyle, border: '2px solid #1565c0' }} value={editForm.kategori} onChange={e => setEditForm({ ...editForm, kategori: e.target.value })} />
                            </td>
                            <td style={{ padding: '6px' }}>
                              <select style={{ ...inputStyle, border: '2px solid #1565c0' }} value={editForm.urun_turu_id ?? ''} onChange={e => setEditForm({ ...editForm, urun_turu_id: e.target.value })}>
                                <option value="">— Atanmamış</option>
                                {urunTurleri.filter(t => t.aktif || t.id === Number(editForm.urun_turu_id)).map(t => (
                                  <option key={t.id} value={t.id}>{t.ad}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '6px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {markalar.filter(mk => mk.aktif).map(mk => {
                                  const sel = (editForm.marka_ids || []).map(Number);
                                  const checked = sel.includes(mk.id);
                                  return (
                                    <label key={mk.id} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={e => {
                                          const next = e.target.checked
                                            ? [...sel, mk.id]
                                            : sel.filter(x => x !== mk.id);
                                          setEditForm({ ...editForm, marka_ids: next });
                                        }}
                                      />
                                      {mk.ad}
                                    </label>
                                  );
                                })}
                                <span style={{ fontSize: '10px', color: '#888', fontStyle: 'italic' }}>
                                  {(editForm.marka_ids || []).length === 0 ? '— Paylaşılan (tüm şubeler)' : 'Sadece seçili markalar'}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: '6px' }}>
                              <input type="number" step="0.01" style={{ ...inputStyle, textAlign: 'right', border: '2px solid #1565c0' }} value={editForm.fiyat} onChange={e => setEditForm({ ...editForm, fiyat: e.target.value })} />
                            </td>
                            <td style={{ padding: '6px' }}>
                              <input type="number" style={{ ...inputStyle, textAlign: 'right', border: '2px solid #1565c0' }} value={editForm.min_stok} onChange={e => setEditForm({ ...editForm, min_stok: e.target.value })} />
                              <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', color: '#666', marginTop: '3px', cursor: 'pointer' }} title="Kilitliyse ABC otomatik hesabı bu min_stok'u ezmez">
                                <input type="checkbox" checked={!!editForm.min_stok_kilit} onChange={e => setEditForm({ ...editForm, min_stok_kilit: e.target.checked })} />
                                🔒 Kilit (ABC ezmesin)
                              </label>
                            </td>
                            <td style={{ padding: '6px' }}>
                              <input type="number" min="0" placeholder="—" style={{ ...inputStyle, textAlign: 'right', border: '2px solid #1565c0' }} value={editForm.raf_omru_gun} onChange={e => setEditForm({ ...editForm, raf_omru_gun: e.target.value })} />
                            </td>
                            <td style={{ padding: '6px' }}>
                              <input type="number" min="0" placeholder="—" title="Boş = varsayılan tedarik süresi (Ayarlar)" style={{ ...inputStyle, textAlign: 'right', border: '2px solid #1565c0' }} value={editForm.tedarik_suresi_gun} onChange={e => setEditForm({ ...editForm, tedarik_suresi_gun: e.target.value })} />
                            </td>
                            <td style={{ padding: '6px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                              <button onClick={handleEditSave} disabled={saving} style={{ padding: '6px 16px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', marginRight: '6px', fontSize: '12px', fontWeight: 'bold' }}>
                                {saving ? '...' : 'Kaydet'}
                              </button>
                              <button onClick={cancelEdit} style={{ padding: '6px 12px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}>
                                İptal
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: '8px' }}>
                              <span style={{ fontFamily: 'monospace', borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.kod || '-'}</span>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.ad}</span>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.birim}</span>
                            </td>
                            <td style={{ padding: '8px' }}>
                              <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.kategori}</span>
                            </td>
                            <td style={{ padding: '8px' }}>
                              {(() => {
                                const t = urunTurleri.find(x => x.id === m.urun_turu_id);
                                if (!t) return <span style={{ color: '#888', fontSize: '11px', fontStyle: 'italic' }}>— Atanmamış</span>;
                                return <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32' }}>{t.ad}</span>;
                              })()}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {(() => {
                                const ids = (m.marka_ids && m.marka_ids.length) ? m.marka_ids : (m.marka_id ? [m.marka_id] : []);
                                if (ids.length === 0) return (
                                  <span style={{ color: '#888', fontSize: '11px', fontStyle: 'italic' }}>— Paylaşılan</span>
                                );
                                return (
                                  <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                    {ids.map(id => {
                                      const mk = markalar.find(x => x.id === id);
                                      return (
                                        <span key={id} style={{
                                          fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                                          borderRadius: 10, background: '#e3f2fd', color: '#1565c0',
                                        }}>{mk ? mk.ad : id}</span>
                                      );
                                    })}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <span style={{ fontFamily: 'monospace', borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>
                                {m.fiyat != null ? `${Number(m.fiyat).toFixed(2)}` : '-'}
                              </span>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.min_stok}</span>
                              {m.min_stok_kilit ? <span title="Kilitli — ABC otomatiği ezmez" style={{ marginLeft: '4px' }}>🔒</span> : null}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.raf_omru_gun != null ? m.raf_omru_gun : '—'}</span>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }} title="Boş = varsayılan tedarik süresi (Ayarlar)">
                              <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>{m.tedarik_suresi_gun != null ? m.tedarik_suresi_gun : '—'}</span>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                              <button onClick={() => startEdit(m)} style={{ padding: '6px 16px', background: '#1565c0', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', marginRight: '6px' }}>
                                Düzenle
                              </button>
                              <button onClick={() => handleDelete(m)} style={{ padding: '6px 12px', background: '#e53935', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}>
                                Sil
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                      {isEditing && (
                        <tr style={{ background: '#fffde7' }}>
                          <td colSpan={11} style={{ padding: '10px 16px', borderBottom: `2px solid ${colors.border}` }}>
                            {renderSasPaneli(m.id)}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'lokasyonlar' && (
            <div>
              <div style={{ marginBottom: '30px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Lokasyon Ekle</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: '12px' }}>Lokasyon Adi</label>
                    <input style={{ width: '100%', padding: '8px' }} value={lokasyonForm.ad} onChange={e => setLokasyonForm({ ...lokasyonForm, ad: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }}>Tip</label>
                    <select style={{ width: '100%', padding: '8px' }} value={lokasyonForm.tip} onChange={e => setLokasyonForm({ ...lokasyonForm, tip: e.target.value })}>
                      <option value="DEPO">Depo</option>
                      <option value="SUBE">Sube</option>
                      <option value="URETIM">Üretim</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px' }} title="Şubeyse markasını seçin. Merkez/üretim/depo için boş bırakın — tüm markaları görür.">
                      Marka <span style={{ color: '#888' }}>(şubeyse)</span>
                    </label>
                    <select style={{ width: '100%', padding: '8px' }} value={lokasyonForm.marka_id} onChange={e => setLokasyonForm({ ...lokasyonForm, marka_id: e.target.value })}>
                      <option value="">— Merkez (tüm markalar)</option>
                      {markalar.filter(m => m.aktif).map(m => (
                        <option key={m.id} value={m.id}>{m.ad}</option>
                      ))}
                    </select>
                  </div>
                  <button onClick={handleSaveLokasyon} style={{ padding: '10px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
                  💡 <strong>Şube</strong> ise markası seçin: o markaya ait + paylaşılan ürünler görünür.
                  <strong>Merkez/Üretim/Depo</strong> için boş bırakın: tüm markaları görür.
                </div>
              </div>

              <h4>Mevcut Lokasyonlar ({lokasyonlar.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8 }}>Tip</th>
                  <th style={{ padding: 8 }}>Marka</th>
                  <th style={{ padding: 8 }} title="Üretim merkezi (commissary) — üretici firma bu lokasyonun markasından türetilir; ham madde/karkas görür">Üretim Merkezi</th>
                </tr></thead>
                <tbody>
                  {lokasyonlar.map(l => (
                    <tr key={l.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: 8 }}><strong>{l.ad}</strong></td>
                      <td style={{ padding: 8 }}>
                        <span style={{ background: '#eee', padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px' }}>{l.tip}</span>
                      </td>
                      <td style={{ padding: 8 }}>
                        <select
                          value={l.marka_id ?? ''}
                          onChange={e => handleUpdateLokasyonMarka(l.id, e.target.value)}
                          style={{ padding: '4px 8px', fontSize: '12px', minWidth: '180px' }}
                        >
                          <option value="">— Merkez (tüm markalar)</option>
                          {markalar.filter(m => m.aktif).map(m => (
                            <option key={m.id} value={m.id}>{m.ad}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!l.uretim}
                          onChange={e => handleUpdateLokasyonUretim(l.id, e.target.checked)}
                          title="Üretim merkezi (commissary) — markası üretici firma olarak kullanılır"
                          style={{ cursor: 'pointer', width: 16, height: 16 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'firmalar' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Firma Ekle</h3>
                <p style={{ fontSize: 13, color: '#666' }}>Firma kodları büyük harfli ve underscore'lu olmalı (örn: TORUNA_KASAP). Mevcut kayıtlarda kullanılan kodları değiştirmeyin.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={firmaForm.kod} onChange={e => setFirmaForm({ ...firmaForm, kod: e.target.value.toUpperCase() })} placeholder="OFIS" /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={firmaForm.ad} onChange={e => setFirmaForm({ ...firmaForm, ad: e.target.value })} placeholder="Ofis" /></div>
                  <div><label style={{ fontSize: 12 }}>Vergi No</label><input style={inputStyle} value={firmaForm.vergi_no} onChange={e => setFirmaForm({ ...firmaForm, vergi_no: e.target.value })} /></div>
                  <div><label style={{ fontSize: 12 }}>Vergi Dairesi</label><input style={inputStyle} value={firmaForm.vergi_dairesi} onChange={e => setFirmaForm({ ...firmaForm, vergi_dairesi: e.target.value })} /></div>
                  <div><label style={{ fontSize: 12 }}>KDV Oranı (%)</label><input type="number" step="0.1" min="0" max="100" style={inputStyle} value={firmaForm.kdv_orani} onChange={e => setFirmaForm({ ...firmaForm, kdv_orani: e.target.value })} placeholder="boş = genel oran" /></div>
                  <div><label style={{ fontSize: 12 }}>Tip</label>
                    <select style={inputStyle} value={firmaForm.tip} onChange={e => setFirmaForm({ ...firmaForm, tip: e.target.value })}>
                      <option value="SIRKET">Şirket</option>
                      <option value="MASRAF_MERKEZI">Masraf Merkezi (OFIS vb.)</option>
                    </select>
                  </div>
                  <button onClick={handleSaveFirma} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
              </div>
              <h4>Mevcut Firmalar ({firmalar.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th><th style={{ padding: 8 }}>Tip</th>
                  <th style={{ padding: 8 }}>Vergi No</th><th style={{ padding: 8 }}>Kullanım</th>
                  <th style={{ padding: 8 }}>Durum</th><th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {firmalar.map(f => {
                    const usage = f.usage || { toplam: 0, fatura: 0, odeme: 0, gider: 0 };
                    return (
                      <tr key={f.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: f.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{f.kod}</td>
                        <td style={{ padding: 8 }}><strong>{f.ad}</strong></td>
                        <td style={{ padding: 8 }}>{f.tip === 'MASRAF_MERKEZI'
                          ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#ede7f6', color: '#5e35b1' }}>Masraf Merkezi</span>
                          : <span style={{ color: '#666', fontSize: 11 }}>Şirket</span>}</td>
                        <td style={{ padding: 8 }}>{f.vergi_no || '-'}</td>
                        <td style={{ padding: 8, fontSize: 11 }} title={`Fatura: ${usage.fatura}, Ödeme: ${usage.odeme}, Gider: ${usage.gider}`}>
                          {usage.toplam > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage.toplam} kayıt</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}
                        </td>
                        <td style={{ padding: 8 }}><span style={{ background: f.aktif ? '#d4edda' : '#f8d7da', color: f.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{f.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('firma', f)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          {f.aktif
                            ? <button onClick={() => handleDeleteFirma(f.id)} style={{ padding: '4px 10px', background: '#ffc107', color: '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Pasifleştir</button>
                            : <button onClick={() => openEditModal('firma', { ...f, aktif: true })} style={{ padding: '4px 10px', background: '#28a745', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Aktifleştir</button>}
                          <button onClick={() => hardDelete('firma', f)} disabled={usage.toplam > 0} title={usage.toplam > 0 ? `${usage.toplam} kayıtta kullanılıyor — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage.toplam > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage.toplam > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'gider-kategorileri' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Gider Kategorisi</h3>
                <p style={{ fontSize: 13, color: '#666' }}>Keywords: Logo/Excel'den gelen gider kalemlerini bu kategoriye otomatik atamak için anahtar kelimeler (virgülle ayır). Örn: "elektrik,doğalgaz,su"</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={giderKatForm.kod} onChange={e => setGiderKatForm({ ...giderKatForm, kod: e.target.value.toUpperCase() })} placeholder="YENI_KATEGORI" /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={giderKatForm.ad} onChange={e => setGiderKatForm({ ...giderKatForm, ad: e.target.value })} /></div>
                  <div><label style={{ fontSize: 12 }}>Renk</label><input type="color" style={{ ...inputStyle, padding: 2, height: 32 }} value={giderKatForm.renk} onChange={e => setGiderKatForm({ ...giderKatForm, renk: e.target.value })} /></div>
                  <div><label style={{ fontSize: 12 }}>TDHP Borç</label><input style={inputStyle} value={giderKatForm.tdhp_borc} onChange={e => setGiderKatForm({ ...giderKatForm, tdhp_borc: e.target.value })} /></div>
                  <div><label style={{ fontSize: 12 }}>TDHP Alacak</label><input style={inputStyle} value={giderKatForm.tdhp_alacak} onChange={e => setGiderKatForm({ ...giderKatForm, tdhp_alacak: e.target.value })} /></div>
                  <button onClick={handleSaveGiderKat} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12 }}>Keywords (virgülle ayır)</label>
                    <input style={inputStyle} value={giderKatForm.keywords} onChange={e => setGiderKatForm({ ...giderKatForm, keywords: e.target.value })} placeholder="elektrik,doğalgaz,su,enerji" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>P&L Bucket (EBITDA Raporu)</label>
                    <select style={inputStyle} value={giderKatForm.pnl_bucket || ''} onChange={e => setGiderKatForm({ ...giderKatForm, pnl_bucket: e.target.value })}>
                      {PNL_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <h4>Mevcut Kategoriler ({giderKategorileri.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8 }}>TDHP</th>
                  <th style={{ padding: 8 }}>P&L Bucket</th>
                  <th style={{ padding: 8 }}>Keywords</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {giderKategorileri.map(k => {
                    const usage = k.usage_count || 0;
                    const bucketLabel = (PNL_BUCKETS.find(b => b.value === (k.pnl_bucket || '')) || {}).label || '—';
                    return (
                      <tr key={k.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: k.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{k.kod}</td>
                        <td style={{ padding: 8 }}><span style={{ display: 'inline-block', width: 12, height: 12, background: k.renk, borderRadius: 2, marginRight: 6, verticalAlign: 'middle' }}></span><strong>{k.ad}</strong></td>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{k.tdhp_borc}/{k.tdhp_alacak}</td>
                        <td style={{ padding: 8, fontSize: 11, color: k.pnl_bucket ? '#0d6efd' : '#999' }}>{bucketLabel}</td>
                        <td style={{ padding: 8, fontSize: 11, color: '#666', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.keywords}>{k.keywords || '-'}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} gider</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: k.aktif ? '#d4edda' : '#f8d7da', color: k.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{k.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('kategori', k)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleGiderKat(k.id, k.aktif)} style={{ padding: '4px 10px', background: k.aktif ? '#ffc107' : '#28a745', color: k.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{k.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('kategori', k)} disabled={usage > 0} title={usage > 0 ? `${usage} gider kaydında kullanılıyor — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'zayi-nedenleri' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Zayi Nedeni</h3>
                <p style={{ fontSize: 13, color: '#666' }}>Zayi/fire kayıtlarında seçilen nedenler. <strong>Açıklama</strong> kullanıcıya dropdown'da ipucu olarak gösterilir (geniş kategori tanımı).</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={zayiNedenForm.kod} onChange={e => setZayiNedenForm({ ...zayiNedenForm, kod: e.target.value.toUpperCase() })} placeholder="BOZULMA" /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={zayiNedenForm.ad} onChange={e => setZayiNedenForm({ ...zayiNedenForm, ad: e.target.value })} placeholder="Bozulma / Son Kullanma" /></div>
                  <div><label style={{ fontSize: 12 }}>Renk</label><input type="color" style={{ ...inputStyle, padding: 2, height: 32 }} value={zayiNedenForm.renk} onChange={e => setZayiNedenForm({ ...zayiNedenForm, renk: e.target.value })} /></div>
                  <div><label style={{ fontSize: 12 }}>Sıra</label><input type="number" style={inputStyle} value={zayiNedenForm.sira} onChange={e => setZayiNedenForm({ ...zayiNedenForm, sira: e.target.value })} placeholder="0" /></div>
                  <button onClick={handleSaveZayiNeden} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 12 }}>Açıklama</label>
                  <input style={inputStyle} value={zayiNedenForm.aciklama} onChange={e => setZayiNedenForm({ ...zayiNedenForm, aciklama: e.target.value })} placeholder="Son kullanma tarihi geçen, bozulan veya kullanılamaz hale gelen ürünler" />
                </div>
              </div>
              <h4>Mevcut Zayi Nedenleri ({zayiNedenleri.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8 }}>Açıklama</th>
                  <th style={{ padding: 8 }}>Renk</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Sıra</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {zayiNedenleri.map(z => {
                    const usage = z.usage_count || 0;
                    return (
                      <tr key={z.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: z.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{z.kod}</td>
                        <td style={{ padding: 8 }}><strong>{z.ad}</strong></td>
                        <td style={{ padding: 8, fontSize: 11, color: '#666', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={z.aciklama}>{z.aciklama || '-'}</td>
                        <td style={{ padding: 8 }}><span style={{ display: 'inline-block', width: 14, height: 14, background: z.renk || '#6c757d', borderRadius: 3, border: '1px solid #ddd', verticalAlign: 'middle' }}></span></td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{z.sira ?? 0}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} kayıt</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: z.aktif ? '#d4edda' : '#f8d7da', color: z.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{z.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('zayi-neden', z)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleZayiNeden(z.id, z.aktif)} style={{ padding: '4px 10px', background: z.aktif ? '#ffc107' : '#28a745', color: z.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{z.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('zayi-neden', z)} disabled={usage > 0} title={usage > 0 ? `Bu neden ${usage} kayıtta kullanılıyor, önce pasifleştirin` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'urun-turleri' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Ürün Türü</h3>
                <p style={{ fontSize: 13, color: '#666' }}>Malzemelere atanan ürün türleri (kırmızı et, balık, kuru gıda…). <strong>SKT/devir eşikleri</strong> son kullanma ve stok devir uyarılarında kullanılır. Boş bırakılan eşikler varsayılana düşer.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={urunTuruForm.kod} onChange={e => setUrunTuruForm({ ...urunTuruForm, kod: e.target.value.toUpperCase() })} placeholder="ET_KIRMIZI" /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={urunTuruForm.ad} onChange={e => setUrunTuruForm({ ...urunTuruForm, ad: e.target.value })} placeholder="Kırmızı Et" /></div>
                  <div><label style={{ fontSize: 12 }} title="SKT'ye bu kadar gün kala sarı uyarı">SKT Sarı (gün)</label><input type="number" min="0" style={inputStyle} value={urunTuruForm.skt_sari_gun} onChange={e => setUrunTuruForm({ ...urunTuruForm, skt_sari_gun: e.target.value })} placeholder="7" /></div>
                  <div><label style={{ fontSize: 12 }} title="SKT'ye bu kadar gün kala kırmızı uyarı">SKT Kırmızı (gün)</label><input type="number" min="0" style={inputStyle} value={urunTuruForm.skt_kirmizi_gun} onChange={e => setUrunTuruForm({ ...urunTuruForm, skt_kirmizi_gun: e.target.value })} placeholder="2" /></div>
                  <div><label style={{ fontSize: 12 }} title="Bu süreden uzun devir = yavaş hareket">Devir Eşik (gün)</label><input type="number" min="0" style={inputStyle} value={urunTuruForm.devir_gun_esik} onChange={e => setUrunTuruForm({ ...urunTuruForm, devir_gun_esik: e.target.value })} placeholder="30" /></div>
                  <div><label style={{ fontSize: 12 }} title="Bu süre hiç hareket yoksa ölü stok">Ölü Stok (gün)</label><input type="number" min="0" style={inputStyle} value={urunTuruForm.olu_stok_gun} onChange={e => setUrunTuruForm({ ...urunTuruForm, olu_stok_gun: e.target.value })} placeholder="90" /></div>
                  <div><label style={{ fontSize: 12 }}>Sıra</label><input type="number" style={inputStyle} value={urunTuruForm.sira} onChange={e => setUrunTuruForm({ ...urunTuruForm, sira: e.target.value })} placeholder="0" /></div>
                  <button onClick={handleSaveUrunTuru} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
              </div>
              <h4>Mevcut Ürün Türleri ({urunTurleri.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>SKT Sarı</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>SKT Kırmızı</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Devir Eşik</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Ölü Stok</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Sıra</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {urunTurleri.map(t => {
                    const usage = t.usage_count || 0;
                    return (
                      <tr key={t.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: t.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{t.kod}</td>
                        <td style={{ padding: 8 }}><strong>{t.ad}</strong></td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{t.skt_sari_gun ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{t.skt_kirmizi_gun ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{t.devir_gun_esik ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{t.olu_stok_gun ?? '-'}</td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{t.sira ?? 0}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} malzeme</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: t.aktif ? '#d4edda' : '#f8d7da', color: t.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{t.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('urun-turu', t)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleUrunTuru(t.id, t.aktif)} style={{ padding: '4px 10px', background: t.aktif ? '#ffc107' : '#28a745', color: t.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{t.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('urun-turu', t)} disabled={usage > 0} title={usage > 0 ? `Bu tür ${usage} malzemede kullanılıyor, önce pasifleştirin` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'stopaj-turleri' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Stopaj Türü</h3>
                <p style={{ fontSize: 13, color: '#666' }}>Gider/ödeme kayıtlarında uygulanan stopaj (vergi kesintisi) türleri. <strong>Oran (%)</strong> kesinti yüzdesidir (örn. kira stopajı 20). <strong>Kod</strong> oluşturulduktan sonra değiştirilemez.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={stopajForm.kod} onChange={e => setStopajForm({ ...stopajForm, kod: e.target.value.toUpperCase() })} placeholder="KIRA_STOPAJ" /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={stopajForm.ad} onChange={e => setStopajForm({ ...stopajForm, ad: e.target.value })} placeholder="Kira Stopajı" /></div>
                  <div><label style={{ fontSize: 12 }} title="Stopaj kesinti yüzdesi">Oran (%)*</label><input type="number" min="0" step="0.01" style={inputStyle} value={stopajForm.oran} onChange={e => setStopajForm({ ...stopajForm, oran: e.target.value })} placeholder="20" /></div>
                  <div><label style={{ fontSize: 12 }}>Sıra</label><input type="number" style={inputStyle} value={stopajForm.sira} onChange={e => setStopajForm({ ...stopajForm, sira: e.target.value })} placeholder="0" /></div>
                  <button onClick={handleSaveStopaj} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
              </div>
              <h4>Mevcut Stopaj Türleri ({stopajTurleri.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Oran (%)</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Sıra</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {stopajTurleri.map(s => {
                    const usage = s.usage_count || 0;
                    return (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: s.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{s.kod}</td>
                        <td style={{ padding: 8 }}><strong>{s.ad}</strong></td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{s.oran != null ? `%${Number(s.oran)}` : '-'}</td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{s.sira ?? 0}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} gider</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: s.aktif ? '#d4edda' : '#f8d7da', color: s.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{s.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('stopaj-turu', s)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleStopaj(s.id, s.aktif)} style={{ padding: '4px 10px', background: s.aktif ? '#ffc107' : '#28a745', color: s.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{s.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('stopaj-turu', s)} disabled={usage > 0} title={usage > 0 ? `${usage} giderde kullanılıyor — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'departmanlar' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Departman</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={departmanForm.kod} onChange={e => setDepartmanForm({ ...departmanForm, kod: e.target.value.toUpperCase() })} /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={departmanForm.ad} onChange={e => setDepartmanForm({ ...departmanForm, ad: e.target.value })} /></div>
                  <button onClick={handleSaveDepartman} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
              </div>
              <h4>Mevcut Departmanlar ({departmanlar.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {departmanlar.map(d => {
                    const usage = d.usage_count || 0;
                    return (
                      <tr key={d.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: d.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{d.kod}</td>
                        <td style={{ padding: 8 }}><strong>{d.ad}</strong></td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} kullanıcı</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: d.aktif ? '#d4edda' : '#f8d7da', color: d.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{d.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('departman', d)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleDepartman(d.id, d.aktif)} style={{ padding: '4px 10px', background: d.aktif ? '#ffc107' : '#28a745', color: d.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{d.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('departman', d)} disabled={usage > 0} title={usage > 0 ? `${usage} kullanıcıda atanmış — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'markalar' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Marka</h3>
                <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
                  Marka kayıtları reçete formundaki dropdown'ı besler. Türkçe karakter farklılıkları (örn. "İnga" vs "Inga") tek standart isimde tutulmalı.
                  <strong> Günlük çalışma saati</strong>, puantajda fazla mesai saatlik ücreti bazıdır (saatlik = aylık brüt ÷ (30 × saat)). Örn. Toruna/Inga 9, AÜK 10.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 12 }}>Kod*</label><input style={inputStyle} value={markaForm.kod} onChange={e => setMarkaForm({ ...markaForm, kod: e.target.value.toUpperCase() })} placeholder="ALTI_USTU_KOFTE" /></div>
                  <div><label style={{ fontSize: 12 }}>Ad*</label><input style={inputStyle} value={markaForm.ad} onChange={e => setMarkaForm({ ...markaForm, ad: e.target.value })} placeholder="Altı Üstü Köfte" /></div>
                  <div><label style={{ fontSize: 12 }}>Günlük Saat</label><input type="number" step="0.5" min="0.5" max="24" style={{ ...inputStyle, textAlign: 'right' }} value={markaForm.gunluk_calisma_saati} onChange={e => setMarkaForm({ ...markaForm, gunluk_calisma_saati: e.target.value })} placeholder="8" /></div>
                  <button onClick={handleSaveMarka} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
              </div>
              <h4>Mevcut Markalar ({markalar.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th>
                  <th style={{ padding: 8, textAlign: 'right' }} title="Fazla mesai saatlik ücreti bazı (saatlik = aylık brüt ÷ 30 × saat)">Günlük Saat</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {markalar.map(m => {
                    const usage = m.usage_count || 0;
                    return (
                      <tr key={m.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: m.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{m.kod}</td>
                        <td style={{ padding: 8 }}><strong>{m.ad}</strong></td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>{(m.gunluk_calisma_saati ?? 8)} saat</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} reçete</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: m.aktif ? '#d4edda' : '#f8d7da', color: m.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{m.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('marka', m)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleMarka(m.id, m.aktif)} style={{ padding: '4px 10px', background: m.aktif ? '#ffc107' : '#28a745', color: m.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{m.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('marka', m)} disabled={usage > 0} title={usage > 0 ? `${usage} reçetede kullanılıyor — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'kredi-kartlari' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Kredi Kartı</h3>
                <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
                  Gider girişi (Kredi Kartı seçilince) ve cari hesap dropdown'ı buradan beslenir. Kart yenilendiğinde
                  eskisini <strong>Pasifleştir</strong> (geçmiş ödeme kayıtları referansı korur), yeni kartı ekle.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: 12 }}>Kod*</label>
                    <input style={inputStyle} value={kartForm.kod} onChange={e => setKartForm({ ...kartForm, kod: e.target.value.toUpperCase() })} placeholder="KART_9577" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Son 4 hane*</label>
                    <input style={inputStyle} value={kartForm.son_dort_hane} onChange={e => setKartForm({ ...kartForm, son_dort_hane: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="9577" maxLength={4} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Banka (ops.)</label>
                    <input style={inputStyle} value={kartForm.banka} onChange={e => setKartForm({ ...kartForm, banka: e.target.value })} placeholder="Garanti, İş Bankası..." />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Kesim Günü (ops.)</label>
                    <input type="number" min={1} max={31} style={inputStyle} value={kartForm.kesim_gunu} onChange={e => setKartForm({ ...kartForm, kesim_gunu: e.target.value })} placeholder="1-31" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Son Ödeme Günü (ops.)</label>
                    <input type="number" min={1} max={31} style={inputStyle} value={kartForm.son_odeme_gunu} onChange={e => setKartForm({ ...kartForm, son_odeme_gunu: e.target.value })} placeholder="1-31" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Takip Başlangıcı (bu tarihten önceki alışlar borca girmez)</label>
                    <input type="date" style={inputStyle} value={kartForm.takip_baslangic_tarihi} onChange={e => setKartForm({ ...kartForm, takip_baslangic_tarihi: e.target.value })} />
                  </div>
                  <button onClick={handleSaveKart} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
              </div>
              <h4>Mevcut Kartlar ({kartlar.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Son 4</th><th style={{ padding: 8 }}>Banka</th>
                  <th style={{ padding: 8 }}>Kesim</th><th style={{ padding: 8 }}>Son Ödeme</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {kartlar.map(k => {
                    const usage = k.usage_count || 0;
                    return (
                      <tr key={k.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: k.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{k.kod}</td>
                        <td style={{ padding: 8 }}><strong>{k.son_dort_hane}</strong></td>
                        <td style={{ padding: 8, color: '#666' }}>{k.banka || '—'}</td>
                        <td style={{ padding: 8, color: '#666' }}>{k.kesim_gunu ? `${k.kesim_gunu}.` : '—'}</td>
                        <td style={{ padding: 8, color: '#666' }}>{k.son_odeme_gunu ? `${k.son_odeme_gunu}.` : '—'}</td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} kayıt</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: k.aktif ? '#d4edda' : '#f8d7da', color: k.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{k.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('kart', k)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleKart(k.id, k.aktif)} style={{ padding: '4px 10px', background: k.aktif ? '#ffc107' : '#28a745', color: k.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{k.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('kart', k)} disabled={usage > 0} title={usage > 0 ? `${usage} kayıtta kullanılıyor — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'odeme-yontemleri' && (
            <div>
              <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '20px', borderRadius: radius.md }}>
                <h3 style={{ marginTop: 0 }}>Yeni Ödeme Yöntemi</h3>
                <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
                  Gider/ödeme ekranlarındaki ödeme yöntemi dropdown'ını besler. <strong>Kod</strong> oluşturulduktan sonra
                  değiştirilemez (referans bütünlüğü). Yöntem kullanımdan kalkınca <strong>Pasifleştir</strong>.
                  Bayraklar muhasebe ve kasa davranışını belirler: <strong>Nakit</strong> / <strong>Çek ister</strong> / <strong>Kart ister</strong> / <strong>Bakiye azaltır</strong>.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: 12 }}>Kod*</label>
                    <input style={inputStyle} value={odemeYontemiForm.kod} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, kod: e.target.value.toUpperCase() })} placeholder="NAKIT" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Ad*</label>
                    <input style={inputStyle} value={odemeYontemiForm.ad} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, ad: e.target.value })} placeholder="Nakit" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Sıra</label>
                    <input type="number" style={inputStyle} value={odemeYontemiForm.sira} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, sira: parseInt(e.target.value, 10) || 0 })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>Stok Etkisi</label>
                    <select style={inputStyle} value={odemeYontemiForm.stok_etkisi} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, stok_etkisi: e.target.value })}>
                      <option value="NONE">Yok</option>
                      <option value="IADE">İade</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>TDHP Borç</label>
                    <input style={inputStyle} value={odemeYontemiForm.tdhp_borc} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, tdhp_borc: e.target.value })} placeholder="320" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12 }}>TDHP Alacak</label>
                    <input style={inputStyle} value={odemeYontemiForm.tdhp_alacak} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, tdhp_alacak: e.target.value })} placeholder="100" />
                  </div>
                  <button onClick={handleSaveOdemeYontemi} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>EKLE</button>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={odemeYontemiForm.nakit_mi} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, nakit_mi: e.target.checked })} />
                    Nakit
                  </label>
                  <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={odemeYontemiForm.cek_ister} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, cek_ister: e.target.checked })} />
                    Çek ister
                  </label>
                  <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={odemeYontemiForm.kart_ister} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, kart_ister: e.target.checked })} />
                    Kart ister
                  </label>
                  <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={odemeYontemiForm.bakiye_azaltir} onChange={e => setOdemeYontemiForm({ ...odemeYontemiForm, bakiye_azaltir: e.target.checked })} />
                    Bakiye azaltır
                  </label>
                </div>
              </div>
              <h4>Mevcut Ödeme Yöntemleri ({odemeYontemleri.length})</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>Kod</th><th style={{ padding: 8 }}>Ad</th><th style={{ padding: 8 }}>Bayraklar</th>
                  <th style={{ padding: 8 }}>Kullanım</th><th style={{ padding: 8 }}>Durum</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>İşlem</th>
                </tr></thead>
                <tbody>
                  {odemeYontemleri.map(y => {
                    const usage = y.usage_count || 0;
                    const tags = [];
                    if (y.nakit_mi) tags.push('Nakit');
                    if (y.cek_ister) tags.push('Çek');
                    if (y.kart_ister) tags.push('Kart');
                    if (y.bakiye_azaltir) tags.push('Bakiye−');
                    if (y.stok_etkisi === 'IADE') tags.push('İade');
                    return (
                      <tr key={y.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: y.aktif ? 1 : 0.5 }}>
                        <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{y.kod}</td>
                        <td style={{ padding: 8 }}><strong>{y.ad}</strong></td>
                        <td style={{ padding: 8 }}>
                          {tags.length === 0 ? <span style={{ color: '#999', fontSize: 11 }}>—</span> : (
                            <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {tags.map(t => (
                                <span key={t} style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 10, background: '#e3f2fd', color: '#1565c0' }}>{t}</span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: 8, fontSize: 11 }}>{usage > 0 ? <span style={{ background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 10 }}>{usage} kayıt</span> : <span style={{ color: '#999' }}>kullanılmamış</span>}</td>
                        <td style={{ padding: 8 }}><span style={{ background: y.aktif ? '#d4edda' : '#f8d7da', color: y.aktif ? '#155724' : '#721c24', padding: '2px 8px', borderRadius: radius.lg, fontSize: 11 }}>{y.aktif ? 'Aktif' : 'Pasif'}</span></td>
                        <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal('odeme-yontemi', y)} style={{ padding: '4px 10px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Düzenle</button>
                          <button onClick={() => handleToggleOdemeYontemi(y.id, y.aktif)} style={{ padding: '4px 10px', background: y.aktif ? '#ffc107' : '#28a745', color: y.aktif ? '#333' : 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>{y.aktif ? 'Pasifleştir' : 'Aktifleştir'}</button>
                          <button onClick={() => hardDelete('odeme-yontemi', y)} disabled={usage > 0} title={usage > 0 ? `${usage} kayıtta kullanılıyor — silinemez` : 'Kalıcı sil'} style={{ padding: '4px 10px', background: usage > 0 ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: 4, cursor: usage > 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>Sil</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'fiyat-band' && canManagePriceBand && (
            <FiyatBandKontrolu />
          )}
        </div>
      </div>

      {/* ─── Düzenle Modal ─────────────────────────────────────── */}
      {editModal && (
        <div onClick={() => setEditModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: radius.md, padding: 24, minWidth: 480, maxWidth: 600, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0 }}>
              {editModal.type === 'firma' && 'Firma Düzenle'}
              {editModal.type === 'kategori' && 'Gider Kategorisi Düzenle'}
              {editModal.type === 'zayi-neden' && 'Zayi Nedeni Düzenle'}
              {editModal.type === 'urun-turu' && 'Ürün Türü Düzenle'}
              {editModal.type === 'stopaj-turu' && 'Stopaj Türü Düzenle'}
              {editModal.type === 'departman' && 'Departman Düzenle'}
              {editModal.type === 'marka' && 'Marka Düzenle'}
              {editModal.type === 'kart' && 'Kredi Kartı Düzenle'}
              {editModal.type === 'odeme-yontemi' && 'Ödeme Yöntemi Düzenle'}
            </h3>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>Not: <strong>Kod</strong> alanı değiştirilemez (mevcut kayıtların referans bütünlüğü için).</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: '#666' }}>Kod (sabit)</label>
                <input style={{ ...inputStyle, background: '#f5f5f5' }} value={editModal.data.kod} disabled readOnly />
              </div>
              {editModal.type === 'kart' ? (
                <>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Son 4 hane*</label>
                    <input style={inputStyle} value={editModal.data.son_dort_hane || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, son_dort_hane: e.target.value.replace(/\D/g, '').slice(0, 4) } })} maxLength={4} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Banka (ops.)</label>
                    <input style={inputStyle} value={editModal.data.banka || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, banka: e.target.value } })} placeholder="Garanti, İş Bankası..." />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Sıra</label>
                    <input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Kesim Günü (1-31, ops.)</label>
                    <input type="number" min={1} max={31} style={inputStyle} value={editModal.data.kesim_gunu ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, kesim_gunu: e.target.value } })} placeholder="Boş = yok" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Son Ödeme Günü (1-31, ops.)</label>
                    <input type="number" min={1} max={31} style={inputStyle} value={editModal.data.son_odeme_gunu ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, son_odeme_gunu: e.target.value } })} placeholder="Boş = yok" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Takip Başlangıcı (bu tarihten önceki alışlar borca girmez)</label>
                    <input type="date" style={inputStyle} value={editModal.data.takip_baslangic_tarihi ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, takip_baslangic_tarihi: e.target.value } })} />
                  </div>
                </>
              ) : (
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Ad*</label>
                  <input style={inputStyle} value={editModal.data.ad || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, ad: e.target.value } })} />
                </div>
              )}

              {editModal.type === 'firma' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Tip</label>
                    <select style={inputStyle} value={editModal.data.tip || 'SIRKET'} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, tip: e.target.value } })}>
                      <option value="SIRKET">Şirket</option>
                      <option value="MASRAF_MERKEZI">Masraf Merkezi (OFIS vb.)</option>
                    </select>
                  </div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Vergi No</label><input style={inputStyle} value={editModal.data.vergi_no || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, vergi_no: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Vergi Dairesi</label><input style={inputStyle} value={editModal.data.vergi_dairesi || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, vergi_dairesi: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>KDV Oranı (%)</label><input type="number" step="0.1" min="0" max="100" style={inputStyle} value={editModal.data.kdv_orani ?? ''} placeholder="boş = genel oran" onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, kdv_orani: e.target.value === '' ? null : Number(e.target.value) } })} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: 12, color: '#666' }}>Adres</label><input style={inputStyle} value={editModal.data.adres || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, adres: e.target.value } })} /></div>
                </>
              )}

              {editModal.type === 'kategori' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Renk</label><input type="color" style={{ ...inputStyle, padding: 2, height: 32 }} value={editModal.data.renk || '#6c757d'} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, renk: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>TDHP Borç</label><input style={inputStyle} value={editModal.data.tdhp_borc || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, tdhp_borc: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>TDHP Alacak</label><input style={inputStyle} value={editModal.data.tdhp_alacak || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, tdhp_alacak: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: 12, color: '#666' }}>Keywords (virgülle ayır)</label><input style={inputStyle} value={editModal.data.keywords || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, keywords: e.target.value } })} /></div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 12, color: '#666' }}>P&L Bucket (EBITDA Raporu)</label>
                    <select style={inputStyle} value={editModal.data.pnl_bucket || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, pnl_bucket: e.target.value } })}>
                      {PNL_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </div>
                </>
              )}

              {editModal.type === 'zayi-neden' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Renk</label><input type="color" style={{ ...inputStyle, padding: 2, height: 32 }} value={editModal.data.renk || '#6c757d'} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, renk: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={{ fontSize: 12, color: '#666' }}>Açıklama</label><input style={inputStyle} value={editModal.data.aciklama || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, aciklama: e.target.value } })} /></div>
                </>
              )}

              {editModal.type === 'urun-turu' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>SKT Sarı (gün)</label><input type="number" min="0" style={inputStyle} value={editModal.data.skt_sari_gun ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, skt_sari_gun: e.target.value === '' ? null : parseInt(e.target.value, 10) } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>SKT Kırmızı (gün)</label><input type="number" min="0" style={inputStyle} value={editModal.data.skt_kirmizi_gun ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, skt_kirmizi_gun: e.target.value === '' ? null : parseInt(e.target.value, 10) } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Devir Eşik (gün)</label><input type="number" min="0" style={inputStyle} value={editModal.data.devir_gun_esik ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, devir_gun_esik: e.target.value === '' ? null : parseInt(e.target.value, 10) } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Ölü Stok (gün)</label><input type="number" min="0" style={inputStyle} value={editModal.data.olu_stok_gun ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, olu_stok_gun: e.target.value === '' ? null : parseInt(e.target.value, 10) } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
                </>
              )}

              {editModal.type === 'stopaj-turu' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Oran (%)*</label><input type="number" min="0" step="0.01" style={inputStyle} value={editModal.data.oran ?? ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, oran: e.target.value === '' ? '' : Number(e.target.value) } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
                </>
              )}

              {editModal.type === 'departman' && (
                <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
              )}

              {editModal.type === 'marka' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }} title="Fazla mesai saatlik ücreti bazı: saatlik = aylık brüt ÷ (30 × saat)">Günlük Çalışma Saati</label>
                    <input type="number" step="0.5" min="0.5" max="24" style={{ ...inputStyle, textAlign: 'right' }} value={editModal.data.gunluk_calisma_saati ?? 8} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, gunluk_calisma_saati: e.target.value === '' ? '' : parseFloat(e.target.value) } })} />
                  </div>
                </>
              )}

              {editModal.type === 'odeme-yontemi' && (
                <>
                  <div><label style={{ fontSize: 12, color: '#666' }}>Sıra</label><input type="number" style={inputStyle} value={editModal.data.sira || 0} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, sira: parseInt(e.target.value, 10) || 0 } })} /></div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666' }}>Stok Etkisi</label>
                    <select style={inputStyle} value={editModal.data.stok_etkisi || 'NONE'} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, stok_etkisi: e.target.value } })}>
                      <option value="NONE">Yok</option>
                      <option value="IADE">İade</option>
                    </select>
                  </div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>TDHP Borç</label><input style={inputStyle} value={editModal.data.tdhp_borc || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, tdhp_borc: e.target.value } })} /></div>
                  <div><label style={{ fontSize: 12, color: '#666' }}>TDHP Alacak</label><input style={inputStyle} value={editModal.data.tdhp_alacak || ''} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, tdhp_alacak: e.target.value } })} /></div>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!editModal.data.nakit_mi} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, nakit_mi: e.target.checked } })} />
                      Nakit
                    </label>
                    <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!editModal.data.cek_ister} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, cek_ister: e.target.checked } })} />
                      Çek ister
                    </label>
                    <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!editModal.data.kart_ister} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, kart_ister: e.target.checked } })} />
                      Kart ister
                    </label>
                    <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={!!editModal.data.bakiye_azaltir} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, bakiye_azaltir: e.target.checked } })} />
                      Bakiye azaltır
                    </label>
                  </div>
                </>
              )}

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={editModal.data.aktif !== false} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, aktif: e.target.checked } })} />
                  Aktif
                </label>
              </div>
            </div>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setEditModal(null)} style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Vazgeç</button>
              <button onClick={saveEditModal} style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterVerilerPage;
